<script setup>
import { ref, onMounted, defineEmits, watch, nextTick, onBeforeUnmount, defineProps, computed } from 'vue';
import axios from 'axios';
import FilePreviewModal from '@/components/modals/file-preview-modal.vue';

const props = defineProps({
  modelValue: {
    type: [Object, null],
    default: null
  },
  fileList: {
    type: Array,
    default: () => []
  },
  useApi: {
    type: Boolean,
    default: true
  },
  displayMode: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Select a file'
  },
  isListing: {
    type: Boolean,
    default: false
  }
});

const files = ref([]);
const selectedFile = ref(null);
const isLoading = ref(false);
const isLoadingFileDetails = ref(false);
const error = ref(null);
const showPreview = ref(false);
const isOpen = ref(false);
const previewMode = ref('preview');
const containerRef = ref(null);

const emit = defineEmits(['update:modelValue']);

const displayedFileName = computed(() => {
  let file = props.modelValue || selectedFile.value;
  if (file && file.name) {
    return file.extension ? `${file.name}.${file.extension}` : file.name;
  }
  
  return props.placeholder || 'Select a file';
});

const fetchFiles = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const response = await axios.get('/api/files');
        files.value = response.data;
    } catch (err) {
        error.value = 'Failed to load files. Please try again.';
        console.error('Error fetching files:', err);
    } finally {
        isLoading.value = false;
    }
};

const toggleDropdown = () => {
    if (props.displayMode) return;
    isOpen.value = !isOpen.value;
};

const handleFileSelect = async (file) => {
    isLoadingFileDetails.value = true;
    error.value = null;
    isOpen.value = false;
    
    try {
        const schema = await axios.get(`/api/files/${file.name}`, {
            params: {
                extension: file.extension
            }
        });
        selectedFile.value = {
            ...file,
            schema: schema.data?.map(col => ({
                name: col.name,
                data_type: col.data_type,
                description: col.description || ''
            })) || []
        };
        emit('update:modelValue', selectedFile.value);
    } catch (err) {
        error.value = 'Failed to load file details. Please try again.';
        console.error('Error fetching file schema:', err);
    } finally {
        isLoadingFileDetails.value = false;
    }
};

const formatFileInfo = (file) => {
    if (!file) {
        return null;
    }

    const schema = Array.isArray(file.schema) 
        ? file.schema.map(col => ({
            name: col.name || col.column_name || '',
            data_type: col.type || col.data_type || 'string',
            description: col.description || ''
        }))
        : [];
    let sizeDisplay = file.size;
    if (typeof file.size === 'number') {
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = file.size;
        let unitIndex = 0;
        
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        
        sizeDisplay = `${size.toFixed(2)} ${units[unitIndex]}`;
    }
    
    const formattedFile = {
        name: file.extension ? `${file.name}.${file.extension}` : file.name || 'Unknown file',
        size: sizeDisplay || file.size || 'Unknown size',
        type: file.type || 'Unknown type',
        path: file.path || '',
        extension: file.extension,
        schema: schema,
        rows: file.rows || 0,
        dataset_id: file.dataset_id || file.id,
        columnDescriptions: file.columnDescriptions || 
            schema.reduce((acc, col) => {
                acc[col.name] = col.description || '';
                return acc;
            }, {})
    };
    
    return formattedFile;
};

const handlePreviewClick = (e) => {
    e.stopPropagation();
    previewMode.value = 'preview';
    showPreview.value = true;

};

const handleEditClick = (e) => {
    e.stopPropagation();
    previewMode.value = 'edit';
    showPreview.value = true;

};

const handleListingSelectClick = (e) => {
    e.stopPropagation();
    previewMode.value = 'listing-select';
    showPreview.value = true;
}

const handleSave = (updatedFileInfo) => {
    
    if (updatedFileInfo.selectedColumns && updatedFileInfo.schema) {
        const filteredSchema = updatedFileInfo.schema.filter(field => 
            updatedFileInfo.selectedColumns.includes(field.name)
        );

        selectedFile.value = {
            ...selectedFile.value,
            ...updatedFileInfo,
            schema: filteredSchema,
            selectedColumns: updatedFileInfo.selectedColumns
        };
    } else {
        selectedFile.value = {
            ...selectedFile.value,
            ...updatedFileInfo
        };
    }

    emit('update:modelValue', selectedFile.value);
    showPreview.value = false;
    
};

const updateDropdownPosition = () => {
    if (!isOpen.value || !containerRef.value) return;
    
    const selectElement = containerRef.value.querySelector('.file-select__select');
    const dropdown = containerRef.value.querySelector('.file-select__dropdown');
    if (dropdown && selectElement) {
        const rect = selectElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = dropdown.offsetHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        
        if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
            dropdown.style.top = `${rect.top - dropdownHeight}px`;
        } else {
            dropdown.style.top = `${rect.bottom}px`;
        }
        
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.width = `${rect.width}px`;
        dropdown.style.maxHeight = '240px';
    }
};

watch(isOpen, (newValue) => {
    if (newValue) {
        nextTick(() => {
            updateDropdownPosition();
            window.addEventListener('scroll', updateDropdownPosition);
            window.addEventListener('resize', updateDropdownPosition);
        });
    } else {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', updateDropdownPosition);
    window.removeEventListener('resize', updateDropdownPosition);
});

onMounted(() => {

    
    if (props.modelValue) {
        selectedFile.value = props.modelValue;
    } else if (props.fileList && props.fileList.length > 0) {
        selectedFile.value = props.fileList[0];
    }
    
    if (props.useApi && (!props.fileList || props.fileList.length === 0)) {
        fetchFiles();
    }
});



watch(() => props.fileList, (newValue) => {
   
    if (newValue && newValue.length > 0 && !selectedFile.value) {
        selectedFile.value = newValue[0];
    }
}, { immediate: true });
</script>

<template>
    <div class="file-select" ref="containerRef">
        <div class="file-select__container">
            <div class="file-select__select" :class="{
                'file-select__select--open': isOpen,
                'file-select__select--has-value': props.modelValue || selectedFile,
                'file-select__select--display-mode': props.displayMode
            }" @click="toggleDropdown">
                <div class="file-select__display">
                    <span v-if="isLoading || isLoadingFileDetails" class="file-select__loading-state">
                        <div class="file-select__spinner"></div>
                        Loading...
                    </span>
                    <span v-else-if="props.modelValue || selectedFile" class="file-select__selected">
                        {{ displayedFileName }}
                    </span>
                    <span v-else class="file-select__placeholder">
                        {{ props.placeholder || 'Select a file' }}
                    </span>
                </div>
                <div class="file-select__caret" v-if="!props.displayMode">
                    <svg 
                        class="file-select__caret-icon"
                        :class="{ 'file-select__caret-icon--open': isOpen }"
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                    >
                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </div>
            </div>

            <div v-if="!props.displayMode && isOpen" class="file-select__dropdown">
                <div class="file-select__options">
                    <div v-if="error" class="file-select__error">
                        {{ error }}
                        <button @click="fetchFiles" class="file-select__retry-btn">
                            Retry
                        </button>
                    </div>

                    <div v-else-if="files.length === 0" class="file-select__empty">
                        No files available
                    </div>

                    <template v-else>
                        <button v-for="file in files" :key="file.path" class="file-select__option"
                            @click="handleFileSelect(file)">
                            <span class="file-select__name">{{ file.extension ? `${file.name}.${file.extension}` : file.name }}</span>
                        </button>
                    </template>
                </div>
            </div>
        </div>
        
        <div v-if="!selectedFile && props.displayMode" class="file-select__debug">
            No file selected. Check console for details.
        </div>   
        <div v-if="isLoadingFileDetails" class="file-select__selected-section file-select__selected-section--loading">
            <span class="file-select__loading-state">
                <div class="file-select__spinner"></div>
                Loading file details...
            </span>
        </div>
        <div v-else-if="props.modelValue || selectedFile" class="file-select__selected-section">
            
            <div class="file-select__file-info">
                <div class="file-select__file-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M2 4C2 2.89543 2.89543 2 4 2H14L20 8V20C20 21.1046 19.1046 22 18 22H4C2.89543 22 2 21.1046 2 20V4Z"
                            fill="currentColor" fill-opacity="0.2" />
                        <path d="M14 2L20 8H15C14.4477 8 14 7.55228 14 7V2Z" fill="currentColor" />
                    </svg>
                </div>
                <div class="file-select__file-details">
                    <span class="file-select__file-name">{{ displayedFileName }}</span>
                </div>
            </div>
            <div class="file-select__actions">
                <button @click="handlePreviewClick" class="file-select__action-btn" title="Preview file">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-width="2"
                            d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z" />
                        <path stroke="currentColor" stroke-width="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                </button>
                <button v-if="!props.isListing" @click="handleEditClick" class="file-select__action-btn" title="Edit file">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z" />
                    </svg>
                </button>
                <button v-if="props.isListing" @click="handleListingSelectClick" class="file-select__action-btn" title="Listing select">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z" />
                    </svg>
                </button>
            </div>
        </div>

        <FilePreviewModal 
            v-if="props.modelValue || selectedFile" 
            :show="showPreview" 
            :fileInfo="formatFileInfo(props.modelValue || selectedFile)"
            :mode="previewMode" 
            @close="showPreview = false" 
            @save="handleSave" 
        />
    </div>
</template>

<style scoped lang="postcss">
.file-select {
    @apply relative w-full;
}

.file-select__container {
    @apply relative z-[999];
}

.file-select__select {
    @apply relative flex items-center justify-between w-full px-4 py-2 rounded-2xl bg-gray-200 dark:bg-gray-900/80 text-gray-900 dark:text-gray-100  border border-gray-200/80 dark:border-gray-700/80 transition-all duration-200 ease-in-out cursor-pointer;

    &:not(.file-select__select--open):hover {
        @apply bg-gray-100 dark:bg-gray-800/90 border-gray-300 dark:border-gray-600 shadow-sm;
    }
}

.file-select__select--open {
    @apply border-indigo-400 dark:border-indigo-400 ring-2 ring-indigo-400/30 dark:ring-indigo-400/20 bg-white dark:bg-gray-900 shadow-md shadow-indigo-500/10;
}

.file-select__display {
    @apply flex-1 min-h-[28px] flex items-center;
}

.file-select__selected {
    @apply text-gray-900 dark:text-white flex items-center text-sm;
}

.file-select__placeholder {
    @apply text-gray-500/70 dark:text-gray-400/70 text-sm flex items-center;
}

.file-select__caret {
    @apply flex items-center ml-2;
}

.file-select__caret-icon {
    @apply w-5 h-5 text-gray-500/70 dark:text-gray-400/70 transition-transform duration-200;
}

.file-select__caret-icon--open {
    @apply transform rotate-180;
}

.file-select__dropdown {
    @apply w-full mt-1
        z-[9999]
        bg-white dark:bg-gray-900 
        border border-gray-200 dark:border-gray-700 
        rounded-2xl shadow-lg 
        overflow-hidden;
    position: fixed;
    max-height: 240px;
    overflow-y: auto;
}

.file-select__options {
    @apply max-h-60 overflow-y-auto;
    max-height: 240px;
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.file-select__options::-webkit-scrollbar {
    width: 6px;
}

.file-select__options::-webkit-scrollbar-track {
    @apply bg-transparent;
}

.file-select__options::-webkit-scrollbar-thumb {
    @apply bg-gray-300 dark:bg-gray-600 rounded-full;
}

.file-select__options::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400 dark:bg-gray-500;
}

.file-select__option {
    @apply w-full px-4 py-2.5 text-left transition-colors duration-150 ease-in-out;

    &:hover {
        @apply bg-indigo-50 dark:bg-indigo-500/10;
    }
}

.file-select__loading {
    @apply p-4 text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2;
}

.file-select__spinner {
    @apply w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-indigo-600 dark:border-t-purple-500 rounded-full animate-spin;
}

.file-select__error {
    @apply p-4 text-red-600 dark:text-red-400 flex flex-col items-center gap-2;
}

.file-select__retry-btn {
    @apply px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors;
}

.file-select__empty {
    @apply px-4 py-3 text-sm text-gray-500 dark:text-gray-400;
}

.file-select__name {
    @apply block text-gray-900 dark:text-white text-sm;
}

.file-select__path {
    @apply block text-xs text-gray-500 dark:text-gray-400;
}

.file-select__selected-section {
    @apply mt-3 p-3 rounded-xl bg-gray-200 dark:bg-gray-900/80  border border-gray-200/80 dark:border-gray-700/80 flex items-center justify-center shadow-sm;
}

.file-select__file-info {
    @apply flex items-center gap-3 flex-1 min-w-0;
}

.file-select__file-icon {
    @apply w-10 h-10 flex-shrink-0 text-indigo-500 dark:text-indigo-400;
}

.file-select__file-details {
    @apply flex flex-col min-w-0;
}

.file-select__file-name {
    @apply text-sm font-medium text-gray-900 dark:text-white truncate;
}

.file-select__file-path {
    @apply text-xs text-gray-500 dark:text-gray-400 truncate;
}

.file-select__actions {
    @apply flex items-center gap-2;
}

.file-select__action-btn {
    @apply p-2 rounded-lg transition-colors duration-150 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400;

    &.file-select__action-btn--danger {
        @apply hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400;
    }
}

.file-select__select--display-mode {
    @apply cursor-default bg-gray-100 dark:bg-gray-800/90;
}

.file-select__debug {
    @apply mt-2 p-2 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg;
}

.file-select__loading-state {
    @apply text-gray-600 dark:text-gray-300 flex items-center gap-2 text-sm;
}

.file-select__selected-section--loading {
    @apply bg-indigo-50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/30;
}

.file-select__selected-section--error {
    @apply bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-800/30;
}

.file-select__error-icon {
    @apply text-red-500 dark:text-red-400 flex-shrink-0;
}
</style>